import { RefCallback, useCallback, useEffect, useMemo, useState } from 'react'

export interface UseActiveSectionProps {
  /**
   * Set this as the active ID before the top section has scrolled
   * into view.
   */
  defaultSectionId?: string
  /**
   * Distance from top of viewport for the intersection line.
   * A section becomes active when it crosses this line.
   * Place a bit below the sticky menu bottom to avoid switching
   * active section when only padding has scrolled into view.
   */
  offset: number
  /**
   * Selector for sections to observe. Starting from sectionContainerRef, if used.
   */
  sectionSelector: string
}

export interface UseActiveSectionResult {
  /**
   * The ID of the active section. Undefined if no defaultSectionId is set
   * and a section hasn't scrolled into view yet.
   */
  activeSectionId: string | undefined
  /**
   * Ref to use for the parent element of the sections.
   */
  sectionContainerRef: RefCallback<HTMLDivElement>
}

export const useActiveMenuSection = ({
  defaultSectionId,
  offset,
  sectionSelector,
}: UseActiveSectionProps): UseActiveSectionResult => {
  const [activeSectionId, setActiveSectionId] = useState<string | undefined>(
    defaultSectionId
  )

  const [sectionContainer, setSectionContainer] = useState<HTMLDivElement | null>(null)

  const sectionContainerRef = useCallback<RefCallback<HTMLDivElement>>(node => {
    setSectionContainer(node)
  }, [])

  useEffect(() => {
    if (sectionContainer) {
      const options = {
        root: null,
        rootMargin: `-${Math.abs(offset)}px 0px 0px 0px`,
        threshold: 0,
      }

      const sections = Array.from(sectionContainer.querySelectorAll(sectionSelector))

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const inView = entry.isIntersecting
          const atTop = entry.boundingClientRect.top < offset

          if (atTop) {
            // the top of the section is above the offset line
            if (inView) {
              // the section scrolls down over the offset line into the viewable area
              if (entry.target.id) {
                setActiveSectionId(entry.target.id)
              }
            } else {
              // the section scrolls up above the offset line, so switch to the next section
              const index = sections.findIndex(section => section === entry.target)
              const nextItem = sections[index + 1]
              if (index >= 0 && nextItem?.id) {
                setActiveSectionId(nextItem.id)
              }
            }
          } else if (entry.target === sections[0]) {
            // top section either scrolled up into view
            // or scrolled down out of view (unstickied the menu)
            setActiveSectionId(entry.target.id)
          }
        })
      }, options)

      // start observing
      sections.forEach(section => observer.observe(section))

      // clean up
      return () => observer?.disconnect()
    }
  }, [offset, sectionContainer, sectionSelector])

  return useMemo(
    () => ({
      activeSectionId,
      sectionContainerRef,
    }),
    [activeSectionId, sectionContainerRef]
  )
}
